import * as React from 'react';
import { authAPI, userAPI } from '../../utils/api';
import { updateSupabaseAuth, clearSupabaseClientCache } from '../../utils/supabase-client';
import { tokenTail, tokenExp } from '../../utils/realtime-debug';

// One-time cleanup: remove any stale GoTrue session that was written to localStorage
// by auth.setSession() calls in a previous deployment. Those sessions use ES256 tokens
// that GoTrueClient cannot auto-refresh, causing 401 errors on every app load.
try { localStorage.removeItem('sb-zsberbzbcluswsutfhul-auth-token'); } catch {}

interface RegistrationData {
  email: string;
  password: string;
  name: string;
  phone: string;
  dateOfBirth: string;
  childrenCount: number;
  location: string;
  bio?: string;
  avatar?: string;
  childGender?: string;
  childAge?: string;
  noChildren?: boolean;
  isPregnant?: boolean;
  selectedInterests?: string[];
  city?: string;
  coordinates?: { lat: number; lng: number };
  children_data?: Array<{ gender: string | null; birth_date: string | null }>;
}

interface AuthContextType {
  registrationData: Partial<RegistrationData>;
  updateRegistrationData: (data: Partial<RegistrationData>) => void;
  clearRegistrationData: () => void;
  currentUser: any | null;
  setCurrentUser: (user: any | null) => void;
  accessToken: string | null;
  setAccessToken: (token: string | null) => void;
  isAuthLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = React.createContext<AuthContextType>({
  registrationData: {},
  updateRegistrationData: () => {},
  clearRegistrationData: () => {},
  currentUser: null,
  setCurrentUser: () => {},
  accessToken: null,
  setAccessToken: () => {},
  isAuthLoading: true,
  isAuthenticated: false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [registrationData, setRegistrationData] = React.useState<Partial<RegistrationData>>(() => {
    // Restore from sessionStorage so navigating back doesn't lose filled data
    try {
      const stored = sessionStorage.getItem('moms_reg_data');
      return stored ? (JSON.parse(stored) as Partial<RegistrationData>) : {};
    } catch {
      return {};
    }
  });
  const [currentUser, setCurrentUser] = React.useState<any | null>(null);
  const [isAuthLoading, setIsAuthLoading] = React.useState(true);
  const [accessToken, setAccessToken] = React.useState<string | null>(
    () => {
      // Load token from localStorage on initialization
      try {
        return localStorage.getItem('access_token');
      } catch (error) {
        console.error('Error loading access token:', error);
        return null;
      }
    }
  );
  const isAuthenticated = Boolean(accessToken && currentUser?.id);

  // Restore persisted session on app startup.
  React.useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      const token = localStorage.getItem('access_token');
      try {
        if (!token) {
          if (isMounted) {
            setCurrentUser(null);
            setAccessToken(null);
            setIsAuthLoading(false);
          }
          return;
        }

        const session = await authAPI.getSession();
        if (!isMounted) return;

        if (session?.user) {
          if (session.user.id) {
            authAPI.setCurrentUserId(session.user.id);
            // Enrich with full profile (avatar_url, city_name, etc.)
            try {
              const profile = await userAPI.getProfile(session.user.id);
              if (isMounted) setCurrentUser({ ...session.user, ...profile });
            } catch {
              if (isMounted) setCurrentUser(session.user);
            }
          } else {
            setCurrentUser(session.user);
          }
        } else {
          authAPI.clearAuth();
          setCurrentUser(null);
          setAccessToken(null);
        }
      } catch (error) {
        console.error('Error restoring session:', error);
        if (!isMounted) return;
        const message = error instanceof Error ? error.message : String(error);
        const isUnauthorized =
          message.includes('401') ||
          message.includes('Not authenticated') ||
          message.includes('JWT') ||
          message.includes('invalid');

        if (isUnauthorized) {
          // Try to refresh before giving up
          try {
            const newSession = await authAPI.refreshSession();
            if (!isMounted) return;
            if (newSession?.user) {
              setAccessToken(newSession.access_token);
              if (newSession.refresh_token) {
                localStorage.setItem('refresh_token', newSession.refresh_token);
              }
              authAPI.setCurrentUserId(newSession.user.id);
              try {
                const profile = await userAPI.getProfile(newSession.user.id);
                if (isMounted) setCurrentUser({ ...newSession.user, ...profile });
              } catch {
                if (isMounted) setCurrentUser(newSession.user);
              }
              return;
            }
          } catch {
            // Refresh also failed — sign out and redirect
          }
          if (!isMounted) return;
          authAPI.clearAuth();
          setCurrentUser(null);
          setAccessToken(null);
          window.location.replace('/login');
          return;
        } else if (token) {
          // Keep persisted token/user id on transient startup failures.
          setAccessToken(token);
        }
      } finally {
        if (isMounted) {
          setIsAuthLoading(false);
        }
      }
    };

    restoreSession();
    return () => {
      isMounted = false;
    };
  }, []);

  // Load from sessionStorage after mount
  React.useEffect(() => {
    try {
      const saved = sessionStorage.getItem('moms_reg_data');
      if (saved) {
        setRegistrationData(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading registration data:', error);
    }
  }, []);

  // Save token to localStorage when it changes; keep realtime clients in sync.
  React.useEffect(() => {
    console.log(`[AuthContext] accessToken changed → …${tokenTail(accessToken)} ${tokenExp(accessToken)} userId=${currentUser?.id ?? 'none'}`);
    try {
      if (accessToken) {
        localStorage.setItem('access_token', accessToken);
        updateSupabaseAuth(accessToken);
      } else {
        console.log('[AuthContext] token cleared → calling clearSupabaseClientCache()');
        localStorage.removeItem('access_token');
        clearSupabaseClientCache();
      }
    } catch (error) {
      console.error('[AuthContext] Error saving access token:', error);
    }
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep user_id in sync for existing API consumers that read from localStorage.
  React.useEffect(() => {
    try {
      if (currentUser?.id) {
        authAPI.setCurrentUserId(currentUser.id);
      }
    } catch (error) {
      console.error('Error saving current user id:', error);
    }
  }, [currentUser?.id]);

  const updateRegistrationData = (data: Partial<RegistrationData>) => {
    setRegistrationData(prev => {
      const updated = { ...prev, ...data };
      // Сохранить в sessionStorage
      try {
        sessionStorage.setItem('moms_reg_data', JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving registration data:', error);
      }
      return updated;
    });
  };

  const clearRegistrationData = () => {
    setRegistrationData({});
    try {
      sessionStorage.removeItem('moms_reg_data');
    } catch (error) {
      console.error('Error clearing registration data:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        registrationData,
        updateRegistrationData,
        clearRegistrationData,
        currentUser,
        setCurrentUser,
        accessToken,
        setAccessToken,
        isAuthLoading,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}